import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SuperadminAccessControlPage from '../../pages/SuperadminAccessControlPage';
import PlatformAccessScreens from './PlatformAccessScreens';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api } from '../../services/api';
import {
  ACCESS_PREFIX,
  accessGroupFor,
  accessViewFor,
  isTenantScopedView,
} from '../../lib/accessControlNav';

// Views that live in the newer platform screens rather than the legacy page.
const PLATFORM_VIEWS = new Set(['directory', 'matrix', 'platform-matrix', 'policies', 'staff']);

/**
 * One shell for every Access Control view. The group is derived from the view,
 * so the old per-view sidebar ids still work as deep links now that they are
 * tabs. Each inner screen brings its own heading, so this adds only the strip
 * and the tenant the tenant-scoped views operate on.
 */
const AccessControlWorkspace = ({ view = 'dashboard', onNavigate }) => {
  const group = accessGroupFor(view);
  const active = accessViewFor(view);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);

  // In the URL so a deep link into a tenant's permissions is reproducible, and
  // so a refresh cannot quietly move the operator to a different tenant.
  const tenantId = Number(searchParams.get('tenant')) || 1;
  const setTenantId = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    params.set('tenant', String(next));
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const scoped = isTenantScopedView(active);

  useEffect(() => {
    if (!scoped || tenants.length) return;
    api.get('/api/access-control/tenants')
      .then(({ data }) => setTenants(data.tenants || []))
      .catch(() => { /* the picker degrades to whatever is in the URL */ });
  }, [scoped, tenants.length]);

  const current = tenants.find((t) => Number(t.id) === tenantId);

  return (
    <div className="p-1">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {group.tabs.length > 1 ? (
          <Tabs
            value={active}
            onValueChange={(next) => onNavigate?.(`${ACCESS_PREFIX}${next}`)}
          >
            <TabsList className="justify-start overflow-x-auto">
              {group.tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : <span />}

        {scoped && (
          <div>
            <p className="mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">
              Editing tenant
            </p>
            <Select value={String(tenantId)} onValueChange={(v) => setTenantId(Number(v))}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder={current?.name || `Tenant ${tenantId}`} />
              </SelectTrigger>
              <SelectContent>
                {(tenants.length ? tenants : [{ id: tenantId, name: `Tenant ${tenantId}` }]).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name || t.slug} · id {t.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {PLATFORM_VIEWS.has(active)
        ? <PlatformAccessScreens view={active} tenantId={tenantId} />
        : <SuperadminAccessControlPage view={active} tenantId={tenantId} />}
    </div>
  );
};

export default AccessControlWorkspace;
